import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AuthorizationServiceRESTUserService, ReadUser, AuthorizationService } from '@sphere/platform';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { FormlyFieldProps } from '@ngx-formly/material/form-field';
import { map, startWith, tap } from 'rxjs/operators';
import { MatFormField, MatLabel, MatHint } from '@angular/material/form-field';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { AutocompleteEnforceSelectionDirective } from '../../directives/autocomplete-force-selection/autocomplete-force-selection.directive';

export type UserSelectorFieldConfig = FormlyFieldConfig<FormlyFieldProps & { userType?: ReadUser.TypeEnum }>;

@Component({
    selector: 'sphere-user-selector',
    templateUrl: './user-selector.component.html',
    styles: [``],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatFormField,
        MatLabel,
        MatAutocomplete,
        MatOption,
        MatInput,
        MatAutocompleteTrigger,
        ReactiveFormsModule,
        AutocompleteEnforceSelectionDirective,
        MatHint
    ]
})
export class UserSelectorComponent extends FieldType<UserSelectorFieldConfig> implements OnInit {
    private authorization = inject(AuthorizationService);
    private usersApi = inject(AuthorizationServiceRESTUserService);

    private readonly destroyRef = inject(DestroyRef);
    public selectedUserControl = new FormControl<string>(null as unknown as string);

    private readonly userCompany = signal<string>(this.authorization.userCompanyCode());
    public readonly userType = signal<ReadUser.TypeEnum | undefined>(undefined);
    public readonly filterText = toSignal<string | null>(this.selectedUserControl.valueChanges.pipe(startWith('')));
    public readonly users = rxResource({
        params: () => ({ userCompany: this.userCompany() }),
        stream: ({ params }) => {
            return this.usersApi
                .getAllUsers({ company: params.userCompany })
                .pipe(map(users => (this.userType ? users.filter(u => u.type === this.userType()) : users)));
        }
    });
    public filteredUsers = computed(() => {
        return this._filter(this.users.value() || [], this.filterText() || '');
    });

    ngOnInit() {
        if (this.props?.userType && typeof this.props.userType === 'string') {
            this.userType.set(this.props.userType);
        }

        if (this.formControl.value) {
            this.selectedUserControl.setValue(this.formControl.value);
        } else {
            this.selectedUserControl.setValue(null);
        }
        this.selectedUserControl.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(userId => {
                    this.formControl.setValue(userId || null);
                })
            )
            .subscribe();
    }

    private _filter(users: ReadUser[], filterText?: ReadUser | string) {
        if (!filterText) {
            return users;
        }
        const _filterText = typeof filterText === 'string' ? filterText : filterText?.username;
        if (!_filterText || typeof _filterText !== 'string' || _filterText.length === 0) {
            return users;
        }
        return users.filter(user => user.username?.toLowerCase().includes(_filterText.toLowerCase()));
    }

    public displayFn(userId?: string | null) {
        return userId || 'Myself';
    }
}
